import { getStorageAccessRequired, requestCookieAccess } from './storageAccess';
import { PRIVATE_LOGIN_STATE, PrivateLoginStateType } from '../constants/private_login';

const MAX_LOGIN_FAILURES = 2;
const MAX_STORAGE_ACCESS_ATTEMPTS = 2;

type LoginHandlerProps = {
  handleLogin: (sso: string) => void;
  sso: string;
  setLoginState: (loginState: PrivateLoginStateType) => void;
  loggedInNotOwner: boolean;
};

export function createLoginHandler({
  handleLogin,
  sso,
  loggedInNotOwner,
  setLoginState,
}: LoginHandlerProps) {
  let authRequested = false;
  let storageAccessProvided = false;
  let storageAccessAttempts = 0;
  let loginFailedCount = 0;

  function onLoginRequested(): void {
    if (!getStorageAccessRequired() || storageAccessProvided) {
      openPopup();
    } else {
      storageAccessAttempts++;

      // Intentionally not using async/await here to avoid
      // Safari blocking popups from async functions
      requestCookieAccess(openPopup)
        .then((cookieAccessGranted: boolean) =>
          cookieAccessGranted ? onCookieAccessProvided() : onCookieAccessFailed(),
        )
        .catch((error) => {
          console.error('Error requesting cookie access:', error);
        });
    }
  }

  function openPopup(): void {
    authRequested = true;
    handleLogin(sso);
  }

  function onCookieAccessProvided(): void {
    storageAccessProvided = true;
    /*
        In the case where browsers grant storage access immediately,
        we still need to verify the user is actually logged in.
        If they aren't logged in, we'll get a failed login event,
        and we'll need to open the popup to allow them to login.
    */
    if (!authRequested) {
      onLoginFailed();
    }

    if (loggedInNotOwner) {
      setLoginState(PRIVATE_LOGIN_STATE.LOGGED_IN_NOT_OWNER);
    }
  }

  /*
        We don't receive an explicit reason why storage access was denied.
        It could be because the user has never logged in, because
        the user has explicitly denied storage access, or because
        an iframe wrapping the embed uses the `sandbox` attribute
        that doesn't include the necessary 'allow-storage-access-by-user-activation'
        flag.
        Because of the ambiguity of the failure, we have to infer
        the reason based on previous user interactions. This is admittedly
        fragile, but it's the best we can do given the current state of the API.
    */
  function onCookieAccessFailed(): void {
    // If cookie access failed AND the user has already tried to login AT LEAST ONCE,
    // then we believe there are no remaining options to access the video through the embed,
    // and we have to direct them to Vimeo.com to watch the video.
    if (checkAllLoginAttemptsExhausted(1)) {
      setLoginState(PRIVATE_LOGIN_STATE.ALL_ACCESS_ATTEMPTS_FAILED);
      return;
    }
    /*
        In case the of storage access being blocked automatically by the browser
        when the user is not logged (they don't have any cookies to grant access to),
        we need to handle the inevitable login failure that will occur once
        they login and we attempt to fetch the logged in config, but it will fail
        because we don't have cookie access yet.

        For that scenario, the user needs to click the login button again. The login failure
        handler will update the UI to indicate they've logged in, and that they need to
        click one more time to view the video.

        SEE - PRIVATE_LOGIN_STATE.LOGGED_IN_STORAGE_ACCESS_REQUIRED
    */
    onLoginFailed();
  }

  function onLoginFailed(): void {
    loginFailedCount++;

    const tooManyStorageAccessAttempts = checkTooManyStorageAccessAttempts();

    if (checkAllLoginAttemptsExhausted()) {
      setLoginState(PRIVATE_LOGIN_STATE.ALL_ACCESS_ATTEMPTS_FAILED);
    } else if (!tooManyStorageAccessAttempts && authRequested) {
      /*
            When the user logs in through the popup,
            but the browser doesn't allow access to cookies,
            the config request will still describe the user as not logged in.

            In that case, we need to re-request storage access
            (and then re-request the config), but this requires
            another click to initiate the storage access request
            since too much time has elapsed since the original login click.

            Note: this assumes that the original login was actually successful.
        */
      setLoginState(PRIVATE_LOGIN_STATE.LOGGED_IN_STORAGE_ACCESS_REQUIRED);
    } else {
      /*
            This is the case where the user has granted storage access,
            but is actually logged out. The original click was used to grant storage access,
            and request the config to verify their login state.

            Since their login state is still "logged out", the user needs to
            click again to actually login. Then they will have access to the video.
        */
      setLoginState(PRIVATE_LOGIN_STATE.STORAGE_ACCESS_GRANTED_LOGIN_UNCONFIRMED);
    }
  }

  function checkTooManyStorageAccessAttempts(): boolean {
    return storageAccessAttempts >= MAX_STORAGE_ACCESS_ATTEMPTS;
  }

  function checkAllLoginAttemptsExhausted(allowedLoginFailures = MAX_LOGIN_FAILURES): boolean {
    const verifiedCookiesCompletelyInaccessible =
      !getStorageAccessRequired() && authRequested && loginFailedCount > 0;

    const tooManyLoginFailures = loginFailedCount >= allowedLoginFailures;

    const tooManyStorageAccessAttempts = checkTooManyStorageAccessAttempts();

    return (
      (tooManyLoginFailures && tooManyStorageAccessAttempts) ||
      verifiedCookiesCompletelyInaccessible
    );
  }

  return {
    onLoginRequested,
  };
}
